import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import { FaSignal, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { BiTimeFive } from "react-icons/bi";
import { LiaLanguageSolid } from "react-icons/lia";
import { GiOpenBook } from "react-icons/gi";
import { LuCalendarDays } from "react-icons/lu";
import { AiOutlineClose, AiFillYoutube, AiFillFacebook } from "react-icons/ai";
import { IoMdAlbums } from "react-icons/io";
import { BsInstagram, BsLinkedin } from "react-icons/bs";
import { CiMail } from "react-icons/ci";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import NotFound from "../NotFound/NotFound";
import Box from "../Box/Box";

import logo from "../../assets/logo.png";
import "./course.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/swiper-bundle.css";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  get_image,
  get_slider,
  parse_course_URL,
  get_course_URL,
} from "../../helper";
import api from "../../axios";
import { useData } from "../../DataContext";
import posterBg from "../../assets/Poster texture seamless 2.png";

const Course = () => {
  // ==========================
  // States
  // ==========================
  const [course, setCourse] = useState({});
  const [covers, setCovers] = useState([]);
  const [cards, setCards] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const [popupImages, setPopupImages] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isSliderPopupOpen, setIsSliderPopupOpen] = useState(false);

  const [setting, setSetting] = useState({});
  const [courses, setCourses] = useState([]);
  // 👇 loader control
  const [isLoading, setIsLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const { id } = useParams();
  const courseSlug = parse_course_URL(id);
  const isMobile = useMediaQuery("(max-width:768px)");

  // ==========================
  // Data Context
  // ==========================
  const { cachedData, updateCachedData } = useData();

  // Cache expiration time (5 minutes)
  const CACHE_EXPIRY = 5 * 60 * 1000;

  // ==========================
  // Fetch Data
  // ==========================
  useEffect(() => {
    const fetchData = async () => {
      const startTime = Date.now();

      try {
        const cachedCourse =
          cachedData.coursesDetails && cachedData.coursesDetails[courseSlug];

        const isCacheValid =
          cachedCourse &&
          cachedCourse.lastFetched &&
          Date.now() - cachedCourse.lastFetched < CACHE_EXPIRY;

        if (isCacheValid) {
          setCourse(cachedCourse.course);
          setCovers(cachedCourse.covers);
          setInstructors(cachedCourse.instructors);
          setCards(cachedCourse.cards);
          setCourses(cachedData.courses || []);
          setSetting(cachedData.setting || {});
        } else {
          // fetch courses list
          if (!cachedData.courses || cachedData.courses.length === 0) {
            const coursesResponse = await api.get("/courses");
            const newCourses = coursesResponse.data.data;
            setCourses(newCourses);
            updateCachedData({ ...cachedData, courses: newCourses });
          } else {
            setCourses(cachedData.courses);
          }

          // fetch setting
          if (
            !cachedData.setting ||
            Object.keys(cachedData.setting).length === 0
          ) {
            const settingResponse = await api.get("/setting", {
              params: { populate: "*" },
            });
            const newSetting = settingResponse.data.data.attributes;
            setSetting(newSetting);
            updateCachedData({ ...cachedData, setting: newSetting });
          } else {
            setSetting(cachedData.setting);
          }

          // resolve slug to course id
          const allCourses = cachedData.courses && cachedData.courses.length > 0
            ? cachedData.courses
            : (await api.get("/courses")).data.data;
          const matched = allCourses.find((c) => get_course_URL(c) === courseSlug);
          if (!matched) {
            setNotFound(true);
            setIsLoading(false);
            return;
          }

          // fetch course details by id
          const courseResponse = await api.get("courses/" + matched.id, {
            params: {
              pagination: { pageSize: 50 },
              populate: {
                instructors: { populate: "image" },
                gallery: { populate: ["image", "images"] },
                home_image: true,
                sliders: true,
                video: true,
                description: { populate: "*" },
                pdf_file: true,
              },
              locale: "en",
            },
          });

          const newCourse = courseResponse.data.data;
          const newCovers = newCourse?.attributes?.sliders?.data || [];
          const newInstructors = newCourse?.attributes?.instructors || [];
          const newCards = newCourse?.attributes?.gallery || [];

          setCourse(newCourse);
          setCovers(newCovers);
          setInstructors(newInstructors);
          setCards(newCards);

          const updatedCoursesDetails = {
            ...(cachedData.coursesDetails || {}),
            [courseSlug]: {
              course: newCourse,
              covers: newCovers,
              instructors: newInstructors,
              cards: newCards,
              lastFetched: Date.now(),
            },
          };

          updateCachedData({
            ...cachedData,
            coursesDetails: updatedCoursesDetails,
          });
        }

        // wait for 5s + all images
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(5500 - elapsed, 0);

        const waitForImages = () =>
          new Promise((resolve) => {
            const images = Array.from(document.images);
            if (images.length === 0) return resolve();
            let loadedCount = 0;
            images.forEach((img) => {
              if (img.complete) {
                loadedCount++;
                if (loadedCount === images.length) resolve();
              } else {
                img.addEventListener("load", () => {
                  loadedCount++;
                  if (loadedCount === images.length) resolve();
                });
                img.addEventListener("error", () => {
                  loadedCount++;
                  if (loadedCount === images.length) resolve();
                });
              }
            });
          });

        setTimeout(async () => {
          await waitForImages();
          setIsLoading(false);
          setFirstLoad(false);
        }, remaining);
      } catch (error) {
        setNotFound(true);
        console.log(error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [courseSlug]);

  if (notFound) return <NotFound />;

  if (isLoading)
    return (
      <div className="loader-container">
        <img className="loader-image" src={logo} alt="Loading..." />
      </div>
    );

  const handleImageClick = (images, index) => {
    setPopupImages(images?.data || []);
    setSliderIndex(index);
    setIsSliderPopupOpen(true);
  };

  const formatDescription = (text) => text?.replace(/\n/g, "<br />");

  const isArabic = (text) => {
    if (!text) return false;
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  // 2. Alternative approach - create a universal image URL handler:
  const getImageUrl = (imgObj) => {
    if (!imgObj) return null;

    // Handle different image structures
    let url = null;

    // Structure 1: {id, attributes: {url}}
    if (imgObj.attributes && imgObj.attributes.url) {
      url = imgObj.attributes.url;
    }
    // Structure 2: {data: {attributes: {url}}}
    else if (
      imgObj.data &&
      imgObj.data.attributes &&
      imgObj.data.attributes.url
    ) {
      url = imgObj.data.attributes.url;
    }
    // Structure 3: direct attributes {url}
    else if (imgObj.url) {
      url = imgObj.url;
    }

    if (!url) return null;

    // Convert relative URLs to absolute
    if (url.startsWith("/")) {
      url = `https://api.sae-grad.com${url}`;
    }

    return url;
  };

  const description = course?.attributes?.description || "";

  const phoneNumber = "+201027097226";
  const whatsappURL = `https://wa.me/${phoneNumber}`;

  const getEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube short link
    if (url.includes("youtu.be")) {
      const videoId = url.split("youtu.be/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // YouTube watch link
    if (url.includes("youtube.com/watch")) {
      const videoId = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // YouTube shorts
    if (url.includes("youtube.com/shorts/")) {
      const videoId = url.split("shorts/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // YouTube live
    if (url.includes("youtube.com/live/")) {
      const videoId = url.split("live/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo link
    if (url.includes("vimeo.com")) {
      const videoId = url.split("vimeo.com/")[1].split("?")[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return url;
  };

  // ==========================
  // Render
  // ==========================
  return (
    <section className="interior">
      {/* Swiper Covers */}
      <div className="intro-image">
        <Swiper
          navigation
          modules={[Navigation, Autoplay, Pagination]}
          autoplay={{ delay: 2000 }}
          pagination={{ clickable: true }}
          className="mySwiper"
        >
          {covers.map((cover, index) => (
            <SwiperSlide key={index}>
              <div className="intro-image-container">
                <img
                  className="animated-coverflow"
                  loading={firstLoad ? "eager" : "lazy"}
                  src={get_slider(cover)}
                  alt="cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Content */}
      <div className="section-container">
        <div className="content">
          {/* Left */}
          <div className="left">
            <div className="media">
              {course?.attributes?.form_url && (
                <div className="video-wrapper">
                  <iframe
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    src={getEmbedUrl(course.attributes.form_url)}
                    title={course?.attributes?.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    loading={firstLoad ? "eager" : "lazy"}
                  />
                </div>
              )}

              <div className="mobile-info">
                <h3>About the course:</h3>
                <p>
                  <FaSignal size={20} /> Level: {course?.attributes?.level}
                </p>
                <p>
                  <BiTimeFive size={20} /> Duration:{" "}
                  {course?.attributes?.duration}
                </p>
                <p>
                  <LiaLanguageSolid size={21} /> Language:{" "}
                  {course?.attributes?.language}
                </p>
                <p>
                  <LuCalendarDays size={20} /> Starting Date:{" "}
                  {course?.attributes?.starting_date}
                </p>
                <p>
                  <GiOpenBook size={20} /> Topics: {course?.attributes?.topics}
                </p>
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="right">
            <div className="book">
              <Box
                textContent={{ discount: course?.attributes?.discount || "" }}
                buttonHref={course?.attributes?.form_url || "#"}
                whatsapp_link={setting?.whatsapp_link}
                confirmationMessage={course?.attributes?.confirmation_message}
              />

              <div className="info">
                <h3>About the course:</h3>
                <p>
                  <FaSignal /> Level: {course?.attributes?.level}
                </p>
                <p>
                  <BiTimeFive /> Duration: {course?.attributes?.duration}
                </p>
                <p>
                  <LiaLanguageSolid /> Language: {course?.attributes?.language}
                </p>
                <p>
                  <LuCalendarDays /> Starting Date:{" "}
                  {course?.attributes?.starting_date}
                </p>
                <p>
                  <GiOpenBook /> Topics: {course?.attributes?.topics}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructors */}
        {instructors.length > 0 && (
          <div className="instructors" id="urban-mobile">
            {instructors.map((instructor) => {
              const dir = isArabic(instructor?.description) ? "rtl" : "ltr";
              return (
                <div
                  key={instructor?.name}
                  className="instructor-card"
                  dir={dir}
                >
                  <div className="image">
                    <img
                      src={get_image(instructor.image)}
                      alt={instructor?.name}
                      loading={firstLoad ? "eager" : "lazy"}
                    />
                  </div>
                  <div className="instructor-info">
                    <h4>
                      {instructor?.title} {instructor?.name}
                    </h4>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: formatDescription(instructor?.description),
                      }}
                      style={{
                        whiteSpace: "pre-line",
                        lineHeight: "1.6",
                        fontSize: isMobile ? "14px" : "18px",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Description Accordion */}
        {Array.isArray(description) && description.length > 0 && (
          <div id="desc">
            {description.map((desc, index) => {
              const itemText = desc?.description ?? "";
              const itemDir = isArabic(itemText) ? "rtl" : "ltr";
              const hasImages = desc?.image?.data && desc.image.data.length > 0;

              return (
                <Accordion key={index} dir={itemDir}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h4
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "14px" : "22px",
                      }}
                    >
                      {desc?.title}
                    </h4>
                  </AccordionSummary>

                  <AccordionDetails>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: formatDescription(desc?.description),
                      }}
                      style={{
                        whiteSpace: "pre-line",
                        lineHeight: "1.6",
                        fontSize: isMobile ? "14px" : "18px",
                      }}
                    />

                    {hasImages && (
                      <div className="description-images-grid">
                        {desc.image.data.map((image, imgIndex) => {
                          const imageUrl = getImageUrl(image);
                          return (
                            <div
                              key={imgIndex}
                              className="description-image-container"
                            >
                              <img
                                src={imageUrl}
                                alt={
                                  image?.attributes?.alternativeText ||
                                  image?.attributes?.name ||
                                  `Description image ${imgIndex + 1}`
                                }
                                loading={firstLoad ? "eager" : "lazy"}
                                className="description-image"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}

        {/* Gallery */}
        {cards.length > 0 && (
          <div className="Gallery">
            <div className="gallery">
              <div className="gallery-container">
                {cards.map((card, index) => (
                  <div
                    className="gallery-card"
                    key={index}
                    onClick={() => handleImageClick(card.images, index)}
                  >
                    <div className="image">
                      <img src={get_image(card.image)} alt="gallery" />
                      <div>
                        <IoMdAlbums className="album" />
                      </div>
                    </div>
                    <div className="card-text">
                      <div className="heading">
                        <h1 style={{ fontWeight: "600" }}>{card.name}</h1>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {isSliderPopupOpen && (
                <div className="slider-popup">
                  <div className="slider-popup-content">
                    <div
                      className="slider-close-icon"
                      onClick={() => setIsSliderPopupOpen(false)}
                    >
                      <AiOutlineClose />
                    </div>

                    <Swiper
                      navigation
                      modules={[Navigation, Pagination]}
                      initialSlide={sliderIndex}
                      className="slider-popup-swiper"
                      pagination={{ clickable: true }}
                    >
                      {popupImages.map((image, index) => (
                        <SwiperSlide key={index}>
                          <div className="slider-popup-image">
                            <img src={get_slider(image)} alt="popup" />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="footer">
          <div className="social">
            <h3>Contact Us</h3>
            <div className="social2">
              <div>
                <a href={whatsappURL} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp /> {phoneNumber}
                </a>
                <a
                  href={setting?.facebook_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AiFillFacebook className="icon" /> Facebook
                </a>
                <a
                  href={setting?.instagram_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BsInstagram className="icon" /> Instagram
                </a>
                <a
                  href="mailto:info@sae-grad.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  <CiMail className="icon" /> Mail
                </a>
              </div>
              <div>
                <a href={setting?.tiktok_link} target="_blank" rel="noreferrer">
                  <FaTiktok className="icon" /> TikTok
                </a>
                <a
                  href={setting?.linkedin_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <BsLinkedin className="icon" /> LinkedIn
                </a>
                <a
                  href={setting?.youtube_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AiFillYoutube className="icon" /> Youtube
                </a>
              </div>
            </div>
          </div>

          <div className="course">
            <h3>Other Programs</h3>
            {[...courses]
              .sort((a, b) => a.attributes.rank - b.attributes.rank) // تصاعدي
              .map((course) => (
                <Link key={course.id} to={`/courses/${get_course_URL(course)}`}>
                  {course?.attributes?.title}
                </Link>
              ))}
          </div>

          <div className="book">
            <Box
              textContent={{ discount: course?.attributes?.discount || "" }}
              buttonHref={course?.attributes?.form_url || "#"}
              whatsapp_link={setting?.whatsapp_link}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Course;
